import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { adminRequest } from "lib/adminSiteApi";

// /admin/today runs about ten bounded reads, so the background poll is every three minutes; coming
// back to the tab or window re-reads at once when the last read is more than a minute old.
const EVERY = 180000, STALE = 60000;
// Capabilities whose counts the menu shows; staff with none of them never poll.
export const COUNT_CAPS = ["crm.read", "commerce.read", "support.manage", "integrations.read"];
export const TodayFeed = createContext(null);
export const useSharedToday = () => useContext(TodayFeed);

// One read of /admin/today on load, then every three minutes while the tab is visible, and on
// return to the tab or window. The menu counts and the Today screen share it. A failed background
// read is ignored: the last counts stay.
export function useTodayFeed(enabled) {
  const [state, setState] = useState({ loading: Boolean(enabled), data: null, error: null });
  const last = useRef(0), live = useRef(true);
  const load = useCallback((manual) => {
    last.current = Date.now();
    if (manual) setState((s) => ({ ...s, loading: true, error: null }));
    return Promise.resolve()
      .then(() => adminRequest("/admin/today"))
      .then((r) => { if (live.current) setState({ loading: false, data: r?.data || null, error: null }); })
      .catch((error) => {
        if (live.current) setState((s) => ({ loading: false, data: s.data, error: manual || !s.data ? error : null }));
      });
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    live.current = true;
    const visible = () => document.visibilityState !== "hidden";
    load(false);
    const timer = setInterval(() => { if (visible()) load(false); }, EVERY);
    const onVisible = () => { if (visible() && Date.now() - last.current >= STALE) load(false); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      live.current = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled, load]);
  const refresh = useCallback(() => load(true), [load]);
  return enabled ? { ...state, refresh } : null;
}

const total = (groups, keys) => {
  const present = keys.filter((k) => groups[k]);
  return present.length
    ? { count: present.reduce((n, k) => n + (groups[k].count || 0), 0), capped: present.some((k) => groups[k].capped) }
    : null;
};
// Counts per sidebar section key; a section is missing when the viewer cannot see its group.
export function sectionCounts(data) {
  const g = data?.groups || {}, n = data?.numbers || {};
  const leads = n.leadsOpen || g.newLeads;
  return {
    leads: leads ? { count: leads.count || 0, capped: Boolean(leads.capped), words: "new leads" } : null,
    sales: (() => { const t = total(g, ["awaitingTransfer", "needsPreparation", "paymentReview"]); return t && { ...t, words: "orders need you" }; })(),
    customers: g.openSupport ? { count: g.openSupport.count || 0, capped: Boolean(g.openSupport.capped), words: "open support requests" } : null,
    system: g.syncFailed ? { count: g.syncFailed.count || 0, capped: Boolean(g.syncFailed.capped), words: "failed TMS messages", tone: "bad" } : null,
  };
}
// The server caps each scan (60 leads, 100 failed messages): show "60+", and never more than "99+".
export const countText = ({ count, capped }) => (count > 99 ? "99+" : capped ? `${count}+` : String(count));

export function NavCount({ value, className = "" }) {
  if (!value?.count) return null;
  return (
    <span className={`ios-count aw-nav-count ${className}`} data-tone={value.tone}>
      <span aria-hidden="true">{countText(value)}</span>
      <span className="aw-sr">{`, ${countText(value)} ${value.words}`}</span>
    </span>
  );
}
